import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Description,
  Medication,
  Science,
  Article,
  Download,
  ExpandMore,
  CheckCircle,
} from "@mui/icons-material";
import { downloadPDF } from "@/fluxvet/services/documentGenerator";
import { useVetAttendance } from "@/fluxvet/contexts/VetAttendanceContext";

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Lista de documentos já assinados digitalmente
 * Exibe os documentos com seus itens associados
 */
export const SignedDocumentsList: React.FC = () => {
  const { methods } = useVetAttendance();
  const signedDocuments = methods.watch("signedDocuments") || [];
  const treatments = methods.watch("treatments") || [];
  const examsOrProcedures = methods.watch("examsOrProcedures") || [];
  const reports = methods.watch("reports") || [];

  // Estado para controlar quais documentos estão expandidos
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());

  /**
   * Alterna expansão de um documento
   */
  const toggleExpand = (uuid: string) => {
    setExpandedDocs((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(uuid)) {
        newSet.delete(uuid);
      } else {
        newSet.add(uuid);
      }
      return newSet;
    });
  };

  /**
   * Obtém o ícone baseado no tipo do documento
   */
  const getDocumentIcon = (type: string) => {
    switch (type) {
      case "treatment":
        return <Medication sx={{ color: "#00A884" }} />;
      case "exam":
        return <Science sx={{ color: "#00A884" }} />;
      case "report":
        return <Article sx={{ color: "#00A884" }} />;
      default:
        return <Description sx={{ color: "#00A884" }} />;
    }
  };

  /**
   * Obtém os nomes dos itens associados ao documento
   */
  const getItemNames = (doc: any) => {
    const names: string[] = [];

    if (doc.type === "treatment") {
      doc.itemIds.forEach((id: string) => {
        const item = treatments.find((t: any) => t.id === id);
        if (item) names.push(item.name);
      });
    } else if (doc.type === "exam") {
      doc.itemIds.forEach((id: string) => {
        const item = examsOrProcedures.find((e: any) => e.id === id);
        if (item) names.push(item.description);
      });
    } else if (doc.type === "report") {
      doc.itemIds.forEach((id: string) => {
        const item = reports.find((r: any) => r.id === id);
        if (item) names.push("Relatório de Atendimento");
      });
    }

    return names;
  };

  /**
   * Formata a data de assinatura
   */
  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Se não há documentos assinados, não exibe nada
  if (signedDocuments.length === 0) {
    return null;
  }

  return (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Stack spacing={2}>
        {/* Cabeçalho */}
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CheckCircle sx={{ color: "#00A884", fontSize: 28 }} />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Documentos Assinados
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {signedDocuments.length} documento{signedDocuments.length > 1 ? "s" : ""}{" "}
              assinado{signedDocuments.length > 1 ? "s" : ""} digitalmente
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Lista de documentos */}
        <Stack spacing={2}>
          {signedDocuments.map((doc: any) => {
            const isExpanded = expandedDocs.has(doc.uuid);
            const itemNames = getItemNames(doc);

            return (
              <Card
                key={doc.uuid}
                sx={{
                  border: "2px solid #00A884",
                  borderRadius: 2,
                  bgcolor: "rgba(0, 168, 132, 0.02)",
                }}
              >
                <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                  {/* Cabeçalho do documento */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={isExpanded ? 1.5 : 0}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
                      {getDocumentIcon(doc.type)}
                      <Box flex={1}>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {doc.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Assinado em {formatDate(doc.signedAt)}
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        label={`${doc.itemIds.length} ${
                          doc.itemIds.length === 1 ? "item" : "itens"
                        }`}
                        size="small"
                        sx={{
                          bgcolor: "#00A884",
                          color: "#fff",
                          fontWeight: 600,
                        }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => downloadPDF(doc.base64, doc.filename)}
                        sx={{
                          color: "#00A884",
                          "&:hover": { bgcolor: "rgba(0, 168, 132, 0.08)" },
                        }}
                      >
                        <Download />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => toggleExpand(doc.uuid)}
                        sx={{
                          color: "#00A884",
                          transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s",
                        }}
                      >
                        <ExpandMore />
                      </IconButton>
                    </Stack>
                  </Stack>

                  {/* Conteúdo expandido - Itens do documento */}
                  <Collapse in={isExpanded}>
                    <Box
                      sx={{
                        pt: 1.5,
                        borderTop: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        textTransform="uppercase"
                      >
                        Itens incluídos:
                      </Typography>
                      <Stack spacing={0.5} mt={1}>
                        {itemNames.map((name, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                bgcolor: "#00A884",
                                display: "inline-block",
                              }}
                            />
                            {name}
                          </Typography>
                        ))}
                      </Stack>

                      {/* UUID do documento */}
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1,
                          bgcolor: "#f5f5f5",
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          UUID: {doc.uuid}
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      </Stack>
    </Paper>
  );
};

export default SignedDocumentsList;

